import { z } from 'zod';
import { uuidSchema } from './common';
import { baseUnitSchema } from './inventory';

const nonnegativeScaled = z.number().int().nonnegative().refine(Number.isSafeInteger);

export const upsertReplenishmentPolicyRequestSchema = z.object({
  expected_version: z.number().int().positive().optional(),
  destination_location_id: uuidSchema,
  preferred_supplier_id: uuidSchema.nullable().default(null),
  planning_horizon_days: z.number().int().min(1).max(365).default(7),
  demand_lookback_days: z.number().int().min(1).max(365).default(28),
  lead_time_days: z.number().int().min(0).max(365).default(1),
  safety_stock_scaled: nonnegativeScaled.default(0),
  minimum_order_scaled: nonnegativeScaled.default(0),
  order_multiple_scaled: z.number().int().positive().refine(Number.isSafeInteger).default(1000),
  max_stock_scaled: nonnegativeScaled.nullable().default(null),
  active: z.boolean().default(true),
});
export type UpsertReplenishmentPolicyRequest = z.infer<
  typeof upsertReplenishmentPolicyRequestSchema
>;

const positionSchema = z.object({
  on_hand_scaled: nonnegativeScaled,
  blocked_scaled: nonnegativeScaled,
  reserved_scaled: nonnegativeScaled,
  usable_available_scaled: nonnegativeScaled,
  in_transit_scaled: nonnegativeScaled,
  expected_receipts_scaled: nonnegativeScaled,
  forecast_demand_scaled: nonnegativeScaled,
  safety_stock_scaled: nonnegativeScaled,
  net_required_scaled: nonnegativeScaled,
});

export const replenishmentRecommendationSchema = z.object({
  policy_id: uuidSchema,
  policy_version: z.number().int().positive(),
  item_id: uuidSchema,
  sku: z.string(),
  item_name: z.string(),
  base_unit: baseUnitSchema,
  destination_location_id: uuidSchema,
  destination_location_name: z.string(),
  planning_horizon_days: z.number().int(),
  effective_horizon_days: z.number().int(),
  demand_lookback_days: z.number().int(),
  lead_time_days: z.number().int(),
  position: positionSchema,
  transfer: z
    .object({
      source_branch_id: uuidSchema,
      source_branch_name: z.string(),
      source_location_id: uuidSchema,
      source_location_name: z.string(),
      qty_scaled: nonnegativeScaled,
    })
    .nullable(),
  minimum_order_scaled: nonnegativeScaled,
  order_multiple_scaled: z.number().int().positive(),
  max_stock_scaled: nonnegativeScaled.nullable(),
  shelf_life_days: z.number().int().positive().nullable(),
  suggested_purchase_scaled: nonnegativeScaled,
  constraint_note: z.string().nullable(),
  preferred_supplier_id: uuidSchema.nullable(),
  preferred_supplier_name: z.string().nullable(),
  open_draft: z
    .object({
      document_type: z.enum(['stock_transfer', 'purchase_request']),
      document_id: uuidSchema,
      document_number: z.string(),
      status: z.string(),
    })
    .nullable(),
});
export type ReplenishmentRecommendation = z.infer<typeof replenishmentRecommendationSchema>;

export const replenishmentListResponseSchema = z.object({
  items: z.array(replenishmentRecommendationSchema),
  as_of: z.string(),
});

export const createReplenishmentDraftRequestSchema = z.object({
  expected_version: z.number().int().positive(),
  action: z.enum(['transfer', 'purchase']),
});
export type CreateReplenishmentDraftRequest = z.infer<typeof createReplenishmentDraftRequestSchema>;
export const createReplenishmentDraftResponseSchema = z.object({
  document_type: z.enum(['stock_transfer', 'purchase_request']),
  document_id: uuidSchema,
  document_number: z.string(),
  status: z.literal('draft'),
});
